import { MarkMotif } from "@mineoptics/brand/logo";
import { Accordion } from "@/components/Accordion";
import { CONTACT_EMAIL, DOCS_URL } from "@/lib/site";

const GROUPS = [
  {
    label: "Product",
    items: [
      {
        question: "What is MineOptics?",
        answer:
          "A web application for building cost models of open-pit mining operations. You manage sites, drillholes and topography, then describe the operation as a scenario: a mine plan, drill and blast, equipment, processing, support services, revenue and lifecycle assumptions. A calculated scenario can be issued as a report, and a producing site can be held against it month by month.",
      },
      {
        question: "What does the drill and blast analysis calculate?",
        answer:
          "For each drill you add: burden, spacing and pattern, stiffness ratio, volume and tonnes per hole, holes, metres and drill hours per year, rigs required, explosives per hole and per year, annual capital and operating cost, and drilling cost per tonne. Rock density and hardness come from the site.",
      },
      {
        question: "How mature is the cashflow projection?",
        answer: (
          <>
            It is built from what you enter: the mine plan, the drill pattern, the fleet, the plant, overheads, grades,
            prices, the capital schedule and tax. It reports NPV and IRR before and after tax and the cost per tonne of
            each cost centre, and it lists anything it could not cost instead of assuming a number. It is a
            scoping-level model that has not yet been reviewed by a mining engineer against a published study,
            documented in the{" "}
            <a href={`${DOCS_URL}/user-guide/cost-model`} className="text-fg underline underline-offset-4">
              cost model guide
            </a>
            , and not a substitute for a study signed by a Qualified Person.
          </>
        ),
      },
      {
        question: "Does it produce a resource estimate?",
        answer:
          "No. You can import a block model from another package, or estimate a simple one from a site's assays by inverse distance or nearest neighbour, read its grade-tonnage curve and work out a scoping-level pit shell. Kriging, variogram modelling, estimation domains and classification are not available, so the result is not a mineral resource estimate.",
      },
      {
        question: "Can I track a producing mine against the study?",
        answer:
          "Yes, month by month. Under Operations you choose an issued report as the plan for a site, record or import each month's tonnes, head grades, operating cost, capital and revenue, and see where the site stands against the plan to date. It compares totals that someone enters; it does not read from equipment or plant systems.",
      },
    ],
  },
  {
    label: "Data",
    items: [
      {
        question: "What can I import?",
        answer:
          "CSV files for collars, downhole surveys, geology intervals, samples, assays, density and QA/QC standards, blanks and duplicates, plus topography points. For these, templates are provided and columns are mapped before an import runs. Block models, a mine schedule and a site's monthly actuals are imported from CSV as well. Excel files are not supported yet.",
      },
      {
        question: "Which coordinate systems are supported?",
        answer:
          "Each site has a projected coordinate reference system identified by an EPSG code; a UTM zone is suggested from the site's position. Local mine grids and custom projections are not supported yet.",
      },
      {
        question: "Can I get results out?",
        answer:
          "A scenario's results, with the warnings and assumptions behind them, export to an Excel workbook and print to PDF. On a paid plan a scenario can also be issued as a report: a numbered revision, frozen with its inputs, that opens as a document and prints. It is a scoping summary, without charts or your letterhead yet. Everything in the dashboard is also available through the HTTP API.",
      },
    ],
  },
  {
    label: "Plans",
    items: [
      {
        question: "Is there a free trial?",
        answer:
          "Yes. A new organization starts on Demo with every module open for 7 days: one project and one site of your own, and a sample project, a fictional copper-gold study that runs from drillholes to an issued report. After 7 days the study tools lock until the organization is on a paid plan. Nothing is deleted: your sites, drillholes, topography and the 3D view stay open, and reports you issued can still be read.",
      },
      {
        question: "Who pays, the user or the company?",
        answer:
          "A subscription belongs to an organization. Every member works under the organization's plan, and only an organization admin can change it.",
      },
      {
        question: "How do I reach you?",
        answer: (
          <>
            Email{" "}
            <a href={`mailto:${CONTACT_EMAIL}`} className="text-fg underline underline-offset-4">
              {CONTACT_EMAIL}
            </a>
            .
          </>
        ),
      },
    ],
  },
];

export function Faq() {
  return (
    <section id="faq" data-theme="light" className="relative overflow-hidden bg-bg">
      <MarkMotif className="pointer-events-none absolute -right-[20rem] -top-[20rem] hidden h-[44rem] w-[44rem] text-fg/25 lg:block" />
      <div className="shell relative py-20 md:py-28">
        <p data-fade className="eyebrow eyebrow-mark mb-6 text-mute">
          Questions
        </p>
        <h2 data-split className="title max-w-[12ch]">
          Straight answers.
        </h2>

        <div className="mt-10 space-y-12 md:mt-14 md:space-y-14">
          {GROUPS.map((group) => (
            <div key={group.label} data-fade className="grid gap-6 lg:grid-cols-[1fr_2fr]">
              <h3 className="subtitle uppercase">{group.label}</h3>
              <Accordion items={group.items} />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
